import {Fieldset, Text} from '@albert-io/atomic';
import cx from 'classnames';
import React from 'react';
import {useShallow} from 'zustand/react/shallow';

import DraftGuessWrapper from 'client/AssignmentPracticeView/DraftGuesses/DraftGuessWrapper';
import MultipleChoiceQuestionStore from 'client/components/QuestionTypes/MultipleChoice/MultipleChoiceQuestion.store';
import {callTargetedAction, setUpStore} from 'client/framework';

import {translatedInstructions, useQuestionTranslationStore} from '../shared/TranslationSection';

import MultipleChoiceOption from './MultipleChoiceOption.react';
import multipleChoiceQuestionActions from './MultipleChoiceQuestion.actions';
import {useMathJaxOverflow} from './useMathJaxOverflow';

import './multiple-choice-question.scss';

interface Props {
  isGuessSubmitted: boolean;
  question: Record<string, any>;
  storeName?: string;
}

function MultipleChoiceInputArea({
  isGuessSubmitted,
  storeName = 'multipleChoiceQuestionStore',
  question
}: Props) {
  const isSelectMultiple = question.isSelectMultiple();
  const store = setUpStore(MultipleChoiceQuestionStore, storeName, question.getId(), question);
  const {ref: mathJaxOverflowRef, hasMathJaxOverflow} = useMathJaxOverflow();

  /**
   * @todo: Having stores hold onto the rubric doesn't make sense since it's available
   * on the question. We should update other types to do this as well. There's probably
   * more that we can be pulling out of the question directly, rathen than writing these
   * things to the store.
   */
  const rubric = question.getMeta().getQuestionRubric?.();
  const correctOptions = rubric ? rubric.get('validResponse') : null;

  // Translation info

  const {translatedQuestionInfo, showTranslations} = useQuestionTranslationStore(
    useShallow((state) => ({
      translatedQuestionInfo: state.translatedQuestionInfo,
      showTranslations: state.showTranslations && state.translationSettings.question
    }))
  );

  const isQuestionPreview = storeName.includes('QuestionPreview');
  const Wrapper = isQuestionPreview ? React.Fragment : DraftGuessWrapper;

  return (
    <Wrapper {...(!isQuestionPreview && {storeName, submitted: isGuessSubmitted})}>
      <div className='mb-x4'>
        <div className='mcq__options-wrapper'>
          <Fieldset>
            {!showTranslations && !isGuessSubmitted && (
              <Text as='legend' size='m' className='mb-x2' bold>
                {isSelectMultiple ? 'Select all that apply' : 'Select one answer'}
              </Text>
            )}
            {showTranslations && !isGuessSubmitted && (
              <Text as='legend' size='m' className='mb-x2' bold>
                {isSelectMultiple
                  ? translatedInstructions.selectMultiple
                  : translatedInstructions.selectOne}
              </Text>
            )}
            <div
              ref={mathJaxOverflowRef}
              className={cx('mcq__options-grid', {
                'mcq__options-grid--mathjax-overflow': hasMathJaxOverflow
              })}
            >
              {question.getOptions().map((option, index) => {
                const optionId = option.getId();
                const translatedText =
                  translatedQuestionInfo?.options?.[index]?.value || option.getValue();

                const handleEliminate = () => {
                  callTargetedAction({
                    name: multipleChoiceQuestionActions.REMOVE_OPTION_FROM_SELECTED,
                    payload: optionId,
                    targetStore: storeName
                  });
                };

                return (
                  <MultipleChoiceOption
                    key={optionId}
                    index={index}
                    option={option}
                    questionId={question.getId()}
                    isGuessSubmitted={isGuessSubmitted}
                    isSelected={store.isSelectedOption(optionId)}
                    isCorrectAnswer={correctOptions && correctOptions.has(optionId)}
                    targetStore={store.getName()}
                    isSelectMultiple={isSelectMultiple}
                    translatedOptionText={translatedText}
                    showTranslation={showTranslations}
                    onEliminate={handleEliminate}
                  />
                );
              })}
            </div>
          </Fieldset>
        </div>
      </div>
    </Wrapper>
  );
}

export {MultipleChoiceInputArea};
